/**
 * @method createBranches 
 * @description creates the branches for a tree structure of the complex selectors
 * @param {object} tree the target tree that will be mutated
 * @param {[string]} paths the individual paths leading to the selectorname
 * @param {string} name the name of the selector itself
 * @param {string} modelToLoad the model that needs to be loaded in the configurator
 * @returns {object} A tree based on the paths available
 */
const createBranches = ( tree, paths, name, modelToLoad ) => {
  let root = tree;
  paths.forEach( ( path, i ) => {
    if ( !root[path] ) {
      root[path] = {};
    }
    root = root[path];
  } );
  root.selector = name;
  root.modelToLoad = modelToLoad;
};

/**
 * @method constructTree 
 * @description will create a tree for the selection based on the configurations given
 * @param {{configurations: object}} param0 an object containing the different configurations
 * @returns {object} a tree that can be used to check which values are available at what time
 */
export const constructTree = ( { configurations } ) => {
  return configurations.reduce( ( tree, config ) => {
    const paths = config.configurationString.split( '#' );
    
    tree[config.name] = paths.reduce( ( current, item ) => {
      let [ label, value ] = item.split( '$' );
      
      current[label] = value;
      return current;
    }, {} );
     
    createBranches( tree, paths, config.name, config.targetSelector );
    createBranches( tree, paths.reverse(), config.name, config.targetSelector );
    
    return tree;
  }, {} );
};

/**
 * @method getSelectedValue 
 * @description locates the value for the variable based on the given parameters
 * @param {string} variable the variable name
 * @param {[string]} selectedValues an array containing previous selected values, when none are selected, the default name is used to determine the selection
 * @param {string} name the default selector name
 * @param {object} tree the tree that contains the path selections
 * @returns {string} the selected value for this variable
 */
export const getSelectedValue = ( variable, selectedValues, name, tree ) => {
  if ( !tree || !tree[name] || !tree[name][variable] ) {
    return null;
  }
  if ( !selectedValues || !selectedValues.length ) {
    // get the default variable for the current selector
    return tree[name][variable];
  }
  if ( selectedValues[0].startsWith( variable + '$' ) ) {
    // if it is the first one, then return the value of the selected item
    return selectedValues[0].split( '$' )[1];
  }
  if ( !tree[selectedValues[0]] ) {
    // no possible assignment
    return null;
  }
  if ( selectedValues.length === 1 ) {
    let branch = tree[selectedValues[0]];
    return Object.keys( branch ).filter( key => key.startsWith( variable + '$' ) )[0].split( '$' )[1];
  }
  if ( selectedValues[1].startsWith( variable + '$' ) ) {
    return selectedValues[1].split( '$' )[1];
  }
  let branch = tree[selectedValues[0]];
  return Object.keys( branch ).filter( key => key.startsWith( variable + '$' ) )[0].split( '$' )[1];
};

/**
 * @method getAvailableValues 
 * @description Gets the available options for a variable based on a tree
 * @param {string} variable The variable for which the values need to be retrieved
 * @param {object} tree The tree containing the paths
 * @param {string?} dependentVariableName Which variable is seen as the "master" variable (thus restricting possible return values)
 * @param {[string]?} selectedValues What values are already selected
 * @param {string?} name The name of the default selection, needed in case the variable & dependentvariable do not match
 * @returns {[string]} A list of strings that are the possible options for this variable
 */
export const getAvailableValues = ( variable, tree, dependentVariableName, selectedValues, name ) => {
  if ( dependentVariableName === variable ) {
    return Object.keys( tree ).where ( k => k.startsWith( variable + '$' ) ).select( k => k.split( '$' )[1] );
  }
  let selected = getSelectedValue( dependentVariableName, selectedValues, name, tree );
  if ( selected === null ) {
    return [];
  }
  return Object.keys( tree[dependentVariableName + '$' + selected] ).where ( k => k.startsWith( variable + '$' ) ).select( k => k.split( '$' )[1] );
};

/**
 * @method getSelector 
 * @description Gets the current selector based on the selected values, tree and all available selectors
 * @param {[string]} selectedValues the selected values
 * @param {object} tree a tree describing the path to the selectors
 * @param {[{name}]} items all the selectors available, which will be matched by name
 * @param {string} name the default selector name
 * @returns {{name}} the matching selector
 */
export const getSelector = ( selectedValues, tree, items, name ) => {
  const branch = selectedValues.reduce( ( current, item ) => current[item], tree );
  const selectorName = ( branch && branch.selector ) || name;
  return { selector: items.firstOrDefault( i => i.name === selectorName ), modelToLoad: branch && branch.modelToLoad };
};